/**
 * What the People screen works out from a group's people and payments: the header sums, the
 * search and sort, a row's amount from the viewer's side, paid-status and the progress bar.
 * Screens only display these.
 */
import { shownCents } from "./shown.ts";
import type { PersonSummary } from "./engine/ledger.ts";

/** A row's amount from the viewer's side: positive = they owe you (flipped for your own row). */
export function rowAmountCents(
  p: PersonSummary,
  viewerPersonId: string | null | undefined,
): number {
  return p.status === "owes" || p.status === "owed"
    ? shownCents(p.balanceMicro) * (viewerPersonId === p.personId ? -1 : 1)
    : 0;
}

/** Header sums: the exact sum of what the rows show, so leftovers under half a cent never add up. */
export function peopleTotals(people: readonly PersonSummary[]): {
  owedSum: number;
  oweSum: number;
} {
  const owing = people.filter((p) => p.status === "owes");
  const owed = people.filter((p) => p.status === "owed");
  return {
    owedSum: owing.reduce((s, p) => s + shownCents(p.balanceMicro), 0),
    oweSum: -owed.reduce((s, p) => s + shownCents(p.balanceMicro), 0),
  };
}

export type PeopleSort = "order" | "owes" | "name";

/** Search by name, hide archived people, then sort. */
export function visiblePeople(
  people: readonly PersonSummary[],
  archived: (personId: string) => boolean,
  query: string,
  sort: PeopleSort,
): PersonSummary[] {
  const needle = query.trim().toLowerCase();
  const list = people.filter(
    (p) => !archived(p.personId) && (!needle || p.name.toLowerCase().includes(needle)),
  );
  if (sort === "owes") return [...list].sort((a, b) => b.balanceMicro - a.balanceMicro);
  if (sort === "name") return [...list].sort((a, b) => a.name.localeCompare(b.name));
  return list;
}

export type PaidStatus = "paid" | "off" | "partial" | "unpaid";

export function paidStatus(p: PersonSummary): PaidStatus {
  if (p.status === "you" || p.status === "paid" || p.status === "owed") return "paid";
  if (p.totalMicro === 0) return "off";
  return p.balanceMicro < p.totalMicro - 5000 ? "partial" : "unpaid";
}

/** How much of their share is paid, 0 to 1. */
export function paidProgress(p: PersonSummary): number {
  return p.totalMicro <= 0
    ? 0
    : Math.max(0, Math.min(1, (p.totalMicro - Math.max(0, p.balanceMicro)) / p.totalMicro));
}

/** One person's numbers in a group, as shown: every row in whole cents, adding up exactly. */
export interface GroupBreakdown {
  /** Their share of the items. */
  itemsCents: number;
  /** Items they paid the store for, for the group. */
  frontedCents: number;
  /** Payments in this group (a group is its own account: only its payments lower it). */
  paidCents: number;
  /** items − fronted − paid, exactly, and the same figure as the row's balance. */
  balanceCents: number;
}

/**
 * A group's People row, opened: items, paid stores, real payments, tutoring pay still in the
 * group, and the balance. The balance is the row's own shown figure and the items line is worked
 * back from it, so the rows always add up to the balance to the cent (it can differ from the
 * items' own rounding by a cent only when both items and paid stores end in half a cent).
 */
export function groupBreakdown(p: PersonSummary): GroupBreakdown {
  const paidCents = p.paidCents;
  const frontedCents = shownCents(p.frontedMicro);
  const balanceCents = shownCents(p.balanceMicro);
  return {
    itemsCents: balanceCents + frontedCents + paidCents,
    frontedCents,
    paidCents,
    balanceCents,
  };
}
